import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import {
  Package, Plus, Trash2, Edit, ArrowLeft, Building2, Wrench, CheckCircle2,
  AlertTriangle, ImagePlus, Boxes, DollarSign
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'spor_malzemesi', label: 'Spor Malzemesi' },
  { id: 'mobilya', label: 'Mobilya' },
  { id: 'elektronik', label: 'Elektronik' },
  { id: 'diger', label: 'Diğer' },
];
const STATUSES = [
  { id: 'saglam', label: 'Sağlam', badge: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  { id: 'tamir', label: 'Tamirde', badge: 'bg-amber-100 text-amber-700 border-amber-200', icon: Wrench },
  { id: 'hurda', label: 'Hurda', badge: 'bg-rose-100 text-rose-700 border-rose-200', icon: AlertTriangle },
];
const catLabel = (id) => (CATEGORIES.find(c => c.id === id) || CATEGORIES[0]).label;
const getStatus = (id) => STATUSES.find(s => s.id === id) || STATUSES[0];
const formatTL = (n) => `₺${(Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const emptyForm = { salon_id: '', name: '', category: 'spor_malzemesi', quantity: '1', unit_price: '', purchase_date: '', status: 'saglam', note: '', photo_url: '' };

const DemirbasTab = ({ salons = [] }) => {
  const [assets, setAssets] = useState([]);
  const [summary, setSummary] = useState([]);
  const [selectedSalon, setSelectedSalon] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchAll = async () => {
    try {
      const [aRes, sRes] = await Promise.all([
        axios.get(`${API}/assets`),
        axios.get(`${API}/assets/summary`),
      ]);
      setAssets(aRes.data?.items || []);
      setSummary(sRes.data?.items || []);
    } catch { toast.error('Demirbaşlar yüklenemedi'); }
  };
  useEffect(() => { fetchAll(); }, []);

  const summaryBySalon = useMemo(() => {
    const m = {};
    summary.forEach(s => { m[s.salon_id] = s; });
    return m;
  }, [summary]);

  const salonAssets = useMemo(
    () => selectedSalon ? assets.filter(a => a.salon_id === selectedSalon.id) : [],
    [assets, selectedSalon]
  );
  const salonTotals = useMemo(() => {
    const qty = salonAssets.reduce((s, a) => s + (a.quantity || 0), 0);
    const val = salonAssets.reduce((s, a) => s + (a.quantity || 0) * (a.unit_price || 0), 0);
    return { qty, val, count: salonAssets.length };
  }, [salonAssets]);

  const grandTotal = useMemo(
    () => summary.reduce((s, x) => s + (x.total_value || 0), 0),
    [summary]
  );

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm, salon_id: selectedSalon?.id || (salons[0]?.id || '') });
    setShowModal(true);
  };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      salon_id: a.salon_id, name: a.name, category: a.category || 'spor_malzemesi',
      quantity: String(a.quantity ?? 1), unit_price: a.unit_price != null ? String(a.unit_price) : '',
      purchase_date: a.purchase_date || '', status: a.status || 'saglam', note: a.note || '', photo_url: a.photo_url || '',
    });
    setShowModal(true);
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, photo_url: res.data?.url || '' }));
      toast.success('Fotoğraf yüklendi');
    } catch { toast.error('Fotoğraf yüklenemedi'); }
    finally { setUploading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.salon_id || !form.name.trim()) { toast.error('Salon ve malzeme adı zorunlu'); return; }
    const payload = {
      salon_id: form.salon_id, name: form.name.trim(), category: form.category,
      quantity: parseInt(form.quantity || '1', 10), unit_price: parseFloat(form.unit_price || '0'),
      purchase_date: form.purchase_date || null, status: form.status,
      note: form.note.trim() || null, photo_url: form.photo_url || null,
    };
    try {
      if (editing) await axios.put(`${API}/assets/${editing.id}`, payload);
      else await axios.post(`${API}/assets`, payload);
      toast.success(editing ? 'Demirbaş güncellendi' : 'Demirbaş eklendi');
      setShowModal(false);
      await fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Kaydedilemedi'); }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Silinsin mi?\n\n${a.name}`)) return;
    try { await axios.delete(`${API}/assets/${a.id}`); toast.success('Silindi'); await fetchAll(); }
    catch { toast.error('Silinemedi'); }
  };

  return (
    <div className="space-y-4" data-testid="demirbas-tab-root">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-900 to-cyan-900 rounded-xl p-4 text-white shadow-lg">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="bg-white/10 p-3 rounded-lg backdrop-blur"><Package className="w-6 h-6" /></div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold">Demirbaş / Envanter</h2>
              <p className="text-xs text-teal-200">Salon bazlı malzeme ve ekipman takibi</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-[11px] text-teal-200 uppercase tracking-wide">Toplam Değer</p>
              <p className="text-lg font-bold" data-testid="demirbas-grand-total">{formatTL(grandTotal)}</p>
            </div>
            <Button onClick={openAdd} className="bg-white text-teal-900 hover:bg-teal-50" data-testid="demirbas-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Demirbaş Ekle
            </Button>
          </div>
        </div>
      </div>

      {!selectedSalon ? (
        /* Salon grid */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="demirbas-salon-grid">
          {salons.length === 0 ? (
            <p className="text-slate-400 text-sm col-span-full text-center py-8">Önce salon eklemelisiniz</p>
          ) : salons.map(s => {
            const sum = summaryBySalon[s.id] || { item_count: 0, total_quantity: 0, total_value: 0 };
            return (
              <button
                key={s.id} type="button" onClick={() => setSelectedSalon(s)}
                className="text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-teal-400 hover:shadow-md transition-all"
                data-testid={`demirbas-salon-${s.id}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center"><Building2 className="w-5 h-5 text-teal-600" /></div>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{s.name}</p>
                    <p className="text-xs text-slate-400">{sum.item_count} kalem</p>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1 text-slate-500"><Boxes className="w-4 h-4" /> {sum.total_quantity} adet</span>
                  <span className="font-bold text-teal-700">{formatTL(sum.total_value)}</span>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        /* Salon detail */
        <div className="space-y-3" data-testid="demirbas-salon-detail">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Button variant="ghost" onClick={() => setSelectedSalon(null)} className="text-slate-600" data-testid="demirbas-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Tüm Salonlar
            </Button>
            <div className="flex items-center gap-3 text-sm">
              <span className="text-slate-500"><strong className="text-slate-800">{salonTotals.count}</strong> kalem</span>
              <span className="text-slate-500"><Boxes className="w-4 h-4 inline mr-0.5" /><strong className="text-slate-800">{salonTotals.qty}</strong> adet</span>
              <span className="flex items-center gap-1 font-bold text-teal-700"><DollarSign className="w-4 h-4" />{formatTL(salonTotals.val)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center"><Building2 className="w-5 h-5 text-teal-600" /></div>
            <h3 className="text-lg font-bold text-slate-800">{selectedSalon.name} — Demirbaşlar</h3>
          </div>

          <Card><CardContent className="p-0">
            {salonAssets.length === 0 ? (
              <p className="text-center py-10 text-slate-400 text-sm">Bu salonda demirbaş kaydı yok</p>
            ) : (
              <div className="divide-y" data-testid="demirbas-asset-list">
                {salonAssets.map(a => {
                  const st = getStatus(a.status);
                  const StIcon = st.icon;
                  return (
                    <div key={a.id} className="flex items-center gap-3 p-3" data-testid={`demirbas-asset-${a.id}`}>
                      {a.photo_url ? (
                        <img src={a.photo_url} alt={a.name} className="w-12 h-12 rounded-lg object-cover border" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center"><Package className="w-5 h-5 text-slate-300" /></div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-sm text-slate-800">{a.name}</p>
                          <Badge className={`border text-[10px] ${st.badge}`}><StIcon className="w-2.5 h-2.5 mr-0.5" />{st.label}</Badge>
                          <Badge variant="outline" className="text-[10px] text-slate-500">{catLabel(a.category)}</Badge>
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-400 flex-wrap">
                          <span>{a.quantity} adet × {formatTL(a.unit_price)}</span>
                          <span className="font-semibold text-teal-600">= {formatTL((a.quantity || 0) * (a.unit_price || 0))}</span>
                          {a.purchase_date && <span>Alım: {new Date(a.purchase_date).toLocaleDateString('tr-TR')}</span>}
                          {a.note && <span className="italic">{a.note}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(a)} className="h-7 w-7 p-0" data-testid={`demirbas-edit-${a.id}`}><Edit className="w-3.5 h-3.5 text-slate-500" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(a)} className="h-7 w-7 p-0" data-testid={`demirbas-delete-${a.id}`}><Trash2 className="w-3.5 h-3.5 text-rose-500" /></Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent></Card>
        </div>
      )}

      {/* Add/Edit modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="demirbas-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Package className="w-5 h-5 text-teal-600" /> {editing ? 'Demirbaş Düzenle' : 'Yeni Demirbaş'}</DialogTitle>
            <DialogDescription>Salon bazlı malzeme/ekipman kaydı</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-3">
            <div>
              <Label>Salon *</Label>
              <Select value={form.salon_id} onValueChange={(v) => setForm({ ...form, salon_id: v })}>
                <SelectTrigger data-testid="demirbas-salon-select"><SelectValue placeholder="Salon seçin" /></SelectTrigger>
                <SelectContent>{salons.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Malzeme Adı *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Örn: File, Top, Çelik dolap" data-testid="demirbas-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kategori</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="demirbas-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Durum</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="demirbas-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Adet</Label><Input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="demirbas-quantity-input" /></div>
              <div><Label>Birim Fiyat (₺)</Label><Input type="number" step="0.01" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} data-testid="demirbas-price-input" /></div>
              <div><Label>Alım Tarihi</Label><Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} data-testid="demirbas-date-input" /></div>
            </div>
            <div><Label>Not</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Ek açıklama..." data-testid="demirbas-note-input" /></div>
            <div>
              <Label>Fotoğraf</Label>
              <div className="flex items-center gap-3 mt-1">
                {form.photo_url ? (
                  <img src={form.photo_url} alt="önizleme" className="w-16 h-16 rounded-lg object-cover border" />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center"><ImagePlus className="w-6 h-6 text-slate-300" /></div>
                )}
                <input ref={fileRef} type="file" accept="image/*" onChange={handlePhoto} className="hidden" data-testid="demirbas-photo-input" />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="demirbas-photo-btn">
                  {uploading ? 'Yükleniyor...' : (form.photo_url ? 'Değiştir' : 'Fotoğraf Yükle')}
                </Button>
                {form.photo_url && <Button type="button" variant="ghost" onClick={() => setForm({ ...form, photo_url: '' })} className="text-rose-500 h-8">Kaldır</Button>}
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">İptal</Button>
              <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700" data-testid="demirbas-save-btn">{editing ? 'Güncelle' : 'Ekle'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DemirbasTab;
